<script setup lang="ts">
import { EventCard } from '~/entities/event-card';
import {
  ChronicleBanner2,
  ChronicleBanner5,
} from '~/entities/chronicle_banners';
import GridTripple from '~/shared/ui/grid/GridTripple.vue';
import {
  CLIENT_EVENTS_AFFICHE_KEY,
  CLIENT_EVENTS_AFFICHE_URL,
} from '~/shared/api/client/publisher/publisher.const';
import {
  type IEventPublisherDataDTO,
  type MappedPublisherEvent,
  mapPublisherEvents,
} from '~/shared/api/client/events/events-publisher.dto';
import type {
  EventListItemDTO,
  EventsPaginatedResponse,
} from '~/shared/api/client/events/events.dto';
import { CLIENT_API_EVENTS_URL } from '~/shared/api/client/events/events.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { EVENT_TIME_FILTERS } from '../../lib/const';
import { useEventsFilters } from '../../lib/useEventsFilters';
import { useEventsAfishaCollectionSeo } from '../../lib/seo';
import EventsBanner from './EventsBanner.vue';

const { $viewport } = useNuxtApp();
const topBannerAfter = computed(() =>
  $viewport.isGreaterOrEquals('tablet') ? 3 : 2,
);

const { timeFilter } = useEventsFilters();
const isPast = computed(() => timeFilter.value === EVENT_TIME_FILTERS.past);
const page = ref(1);
const previousPagesEvents = ref<MappedPublisherEvent[]>([]);

const { data, status, refresh } = useCustomFetch<
  IEventPublisherDataDTO[] | EventsPaginatedResponse<EventListItemDTO>
>(() => (isPast.value ? CLIENT_API_EVENTS_URL : CLIENT_EVENTS_AFFICHE_URL), {
  key: `${CLIENT_EVENTS_AFFICHE_KEY}_list`,
  watch: false,
  query: computed(() =>
    isPast.value
      ? {
          time_filter: EVENT_TIME_FILTERS.past,
          page: page.value,
          page_size: 20,
        }
      : {},
  ),
});

watch(isPast, () => {
  page.value = 1;
  previousPagesEvents.value = [];
  void refresh();
});

const filteredEvents = computed<MappedPublisherEvent[]>(() => {
  if (status.value !== 'success') {
    return isPast.value ? previousPagesEvents.value : [];
  }
  if (isPast.value) {
    const events =
      data.value && !Array.isArray(data.value) ? data.value.results : [];
    return [
      ...previousPagesEvents.value,
      ...events.map((event) => ({
        event,
        imageUrl: event.image?.photo?.file_url ?? null,
        place: event.place ?? '',
      })),
    ];
  }
  const mappedEvents = Array.isArray(data.value)
    ? mapPublisherEvents(data.value)
    : [];
  if (timeFilter.value === EVENT_TIME_FILTERS.all) {
    return mappedEvents;
  }
  const now = new Date();
  return mappedEvents.filter((item) => new Date(item.event.end_time) >= now);
});

const hasNextPage = computed(
  () =>
    isPast.value &&
    data.value &&
    !Array.isArray(data.value) &&
    Boolean(data.value.next),
);

function loadMore(): void {
  if (status.value !== 'success' || !hasNextPage.value) {
    return;
  }
  previousPagesEvents.value = filteredEvents.value;
  page.value += 1;
  void refresh();
}

useEventsAfishaCollectionSeo(filteredEvents);
</script>

<template>
  <div :class="$style.root">
    <div
      v-if="status === 'pending' && !filteredEvents.length"
      :class="$style.loader"
    >
      <Loader />
    </div>

    <template v-else>
      <EventsBanner v-if="timeFilter !== EVENT_TIME_FILTERS.past" />

      <template v-if="filteredEvents.length">
        <GridTripple :class="$style.main__grid">
          <template
            v-for="(item, index) in filteredEvents"
            :key="item.event.id"
          >
            <ChronicleBanner2
              v-if="index === topBannerAfter"
              :class="$style.banner_full"
            />
            <GridItemTripple>
              <EventCard
                :event="item.event"
                :image-url="item.imageUrl"
                :place="item.place"
              />
            </GridItemTripple>
          </template>
        </GridTripple>

        <ChronicleBanner5 />
      </template>
    </template>

    <div v-if="status === 'error'" :class="$style.state" role="alert">
      <p>Не удалось загрузить мероприятия</p>
      <Button theme="secondary" size="xl" @click="refresh()">
        Попробовать снова
      </Button>
    </div>
    <div
      v-else-if="isPast && status === 'success' && !filteredEvents.length"
      :class="$style.state"
    >
      <p>Нет прошедших мероприятий</p>
    </div>

    <div v-else-if="hasNextPage" :class="$style.footer">
      <Button
        theme="secondary"
        size="xl"
        :is-loading="status === 'pending'"
        @click="loadMore"
      >
        Загрузить ещё
      </Button>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.loader {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.root .main__grid {
  @media (min-width: 768px) and (max-width: 1379px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 480px) and (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.banner_full {
  grid-column: 1 / -1;
}

.footer {
  padding: 20px;
}

.state {
  padding: 20px;
  text-align: center;
}
</style>
